import {$isListItemNode, $isListNode} from '@lexical/list';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isTableCellNode} from '@lexical/table';
import {$findMatchingParent} from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import {useEffect} from 'react';

/**
 * Plugin to allow TAB indentation of lists inside table cells.
 *
 * By default, when inside a table cell, TAB navigates to the next cell.
 * This plugin intercepts TAB when the cursor is inside a list node
 * and dispatches indent/outdent commands instead, allowing normal
 * list indentation behavior.
 */
export const ListTabOverridePlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (event: KeyboardEvent | null) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();

        // check if we're in a table
        const tableCellNode = $findMatchingParent(anchorNode, $isTableCellNode);
        if (!tableCellNode) {
          return false;
        }

        // check if the node is a list
        const listNode = $findMatchingParent(
          anchorNode,
          (node) => $isListNode(node) || $isListItemNode(node)
        );

        if (!listNode) {
          return false;
        }

        event?.preventDefault();

        if (event?.shiftKey) {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }

        return true;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor]);

  return null;
};
